import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Monitor, Eye, EyeSlash, SignIn, CircleNotch } from "@phosphor-icons/react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to TSR Remote Control");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden">
      {/* Background Image - Left Side */}
      <div className="hidden lg:block lg:w-1/2 xl:w-3/5 relative">
        <img 
          src="https://images.unsplash.com/photo-1709334197341-122c6fd5ae1d?auto=format&fit=crop&w=2000&q=80" 
          alt="Abstract Network"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[#D50C2D]/5" />
        
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 p-12 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent">
          <h2 className="text-3xl xl:text-4xl font-bold text-white mb-3">Remote Device Management</h2>
        </div>
      </div>
      
      {/* Login Form - Right Side */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 sm:p-8 md:p-12 bg-[#0a0a0a]">
        <div className="w-full max-w-[400px]">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-14 h-14 bg-gradient-to-br from-[#D50C2D] to-[#9a0920] rounded-xl flex items-center justify-center shadow-lg shadow-[#D50C2D]/20">
              <Monitor size={30} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-2xl tracking-tight">TSR Remote</h1>
              <p className="text-xs text-[#52525b] uppercase tracking-widest">Control System</p>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-2">Willkommen zurück</h2>
            <p className="text-[#71717a] text-sm">Melden Sie sich an, um fortzufahren</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#a1a1aa] text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#141416] border-white/10 text-white placeholder:text-[#52525b] focus:border-[#D50C2D] focus:ring-[#D50C2D]/20 h-12 text-base"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#a1a1aa] text-sm font-medium">Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#141416] border-white/10 text-white placeholder:text-[#52525b] focus:border-[#D50C2D] focus:ring-[#D50C2D]/20 pr-12 h-12 text-base"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525b] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#D50C2D] to-[#b80a26] hover:from-[#b80a26] hover:to-[#9a0920] text-white h-12 font-semibold text-base transition-all duration-200 shadow-lg shadow-[#D50C2D]/25 mt-4"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <CircleNotch size={22} className="animate-spin" />
              ) : (
                <>
                  <SignIn size={20} className="mr-2" />
                  Anmelden
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-white/5 text-center">
            <p className="text-[#3f3f46] text-xs">
              TSR Technologies GmbH © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
